// API Configuration - Centralized endpoint management
// This file consolidates all API URLs and configuration

import { getToken } from './common'

// Base API URL - can be easily changed for different environments
export const API_BASE_URL = 'https://bpubm.com/api'

// API Endpoints - organized by functionality
export const API_ENDPOINTS = {
  // Authentication & User Management
  AUTH: {
    LOGIN: `${API_BASE_URL}/login`,
    LOGOUT: `${API_BASE_URL}/logout`,
    REGISTER: `${API_BASE_URL}/register`,
    GET_PROFILE: `${API_BASE_URL}/get-profile`,
    UPDATE_USER_PROFILE: `${API_BASE_URL}/update-user-profile`,
    GET_USERS: `${API_BASE_URL}/get-users`,
    LIST_COGNITO_GROUPS: `${API_BASE_URL}/user-groups`,
    CONFIRM_USER: `${API_BASE_URL}/confirm-user`,
    GET_PENDING_USERS: `${API_BASE_URL}/pending-users`,
  },

  // Book Management
  BOOKS: {
    LIST_BOOKS: `${API_BASE_URL}/list-books`,
    GET_BOOK_REPO: `${API_BASE_URL}/books`,
    INSERT_BOOK_REPO: `${API_BASE_URL}/books`,
    UPDATE_BOOK_REPO: `${API_BASE_URL}/books`, // Uses PUT method with {repoId} path parameter
    REFRESH_BOOK: `${API_BASE_URL}/refresh-book`,
    LIST_UNCLAIMED_BOOK_REPOS: `${API_BASE_URL}/github-unclaimed-book-repos`,
    GET_COMMIT_HISTORY: `${API_BASE_URL}/books`, // Will be appended with /{repoId}/commit-history
  },

  // Template Management
  TEMPLATES: {
    LIST_TEMPLATES: `${API_BASE_URL}/list-templates`,
    GET_TEMPLATE_REPO: `${API_BASE_URL}/templates`,
    INSERT_TEMPLATE_REPO: `${API_BASE_URL}/templates`,
    REFRESH_TEMPLATES: `${API_BASE_URL}/refresh-templates`,
    REFRESH_TEMPLATE: `${API_BASE_URL}/refresh-template`,
    LIST_UNCLAIMED_TEMPLATE_REPOS: `${API_BASE_URL}/github-unclaimed-template-repos`,
  },

  // GitHub Integration
  GITHUB: {
    GET_GITHUB_APPS: `${API_BASE_URL}/github-apps`,
    GET_GITHUB_APP_METADATA: `${API_BASE_URL}/github-apps`,
  },

  // Audit & Logging
  AUDIT: {
    GET_LOGS: `${API_BASE_URL}/audit-logs`,
    GET_EVENTS: `${API_BASE_URL}/events`,
  },

  // Admin & Monitoring
  ADMIN: {
    SEARCH_PDF_JOBS: `${API_BASE_URL}/search-pdf-jobs`,
  },

  // Contact & Support
  CONTACT: {
    SUBMIT_FORM: `${API_BASE_URL}/contact-form`,
  },

  // Notifications (NOTIFICATIONS.GET_NOTIFICATIONS)
  NOTIFICATIONS: {
    GET_NOTIFICATIONS: `${API_BASE_URL}/notifications`,
  },

  // PDF/Build Status
  BUILD: {
    MANUAL_JOB: `${API_BASE_URL}/queue-manual-job`,
    RUN_CHAPTER_SPELL_CHECK: `${API_BASE_URL}/run-chapter-spell-check`,
    RUN_CHAPTER_PLAGIARISM_ANALYSIS: `${API_BASE_URL}/run-chapter-plagiarism-analysis`,
    STATUS: `${API_BASE_URL}/build-status`,
    GET_JOB_METRICS: `${API_BASE_URL}/job-metrics`,
    LATEST_CHAPTER_PDFS: `${API_BASE_URL}/latest-chapter-pdfs`,
    PDF_DOWNLOAD_URL: `${API_BASE_URL}/pdf-download-url`,
    ACTIVE_BUILDS: `${API_BASE_URL}/active-builds`,
  },

  // System & Maintenance
  SYSTEM: {
    DAILY_MAINTENANCE: `${API_BASE_URL}/daily-maintenance`,
    GITHUB_WEBHOOK: `${API_BASE_URL}/github-webhook`,
    PROCESS_COMMIT_FOLDERS: `${API_BASE_URL}/process-commit-folders`,
    HEALTH: `${API_BASE_URL}/health`,
  },

  // Admin Settings
  ADMIN_SETTINGS: {
    GET_ADMIN_SETTINGS: `${API_BASE_URL}/admin-settings`,
    UPDATE_ADMIN_SETTINGS: `${API_BASE_URL}/admin-settings`,
  },

  // Dashboard & Analytics
  DASHBOARD: {
    GET_STATS: `${API_BASE_URL}/dashboard/stats`,
    GET_BUSINESS_METRICS: `${API_BASE_URL}/business-metrics`,
    GET_PERFORMANCE_METRICS: `${API_BASE_URL}/performance-metrics`,
    GET_USER_EXPERIENCE_METRICS: `${API_BASE_URL}/user-experience-metrics`,
  },

  // User Analytics & Tracking
  ANALYTICS: {
    LOG_PAGE_VIEW: `${API_BASE_URL}/analytics/page-view`,
    LOG_SEARCH: `${API_BASE_URL}/analytics/search`,
    LOG_FEATURE_USAGE: `${API_BASE_URL}/analytics/feature-usage`,
    LOG_FILTER_CHANGE: `${API_BASE_URL}/analytics/filter-change`,
    LOG_BUTTON_CLICK: `${API_BASE_URL}/analytics/button-click`,
  },
}

// Helper function to add timestamp to any URL
export const addTimestamp = (url: string): string => {
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}ts=${Date.now()}`
}

// Helper function to get API URL with timestamp
export const getApiUrl = (endpoint: string, addTs: boolean = true): string => {
  return addTs ? addTimestamp(endpoint) : endpoint
}

// Define types for the apiRequest function
interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: any
  headers?: Record<string, string>
  [key: string]: any
}

export const apiRequest = (url: string, options: ApiRequestOptions = {}) => {
  const { method = 'GET', body, ...restOptions } = options;

  const headers: Record<string, string> = {
    'Authorization': getToken(),
    ...restOptions.headers
  };

  // Only add Content-Type for requests with body
  if (body && (method === 'POST' || method === 'PUT')) {
    headers['Content-Type'] = 'application/json';
  }

  return fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    ...restOptions
  });
};

// Type definitions for better TypeScript support
export type ApiEndpoint = keyof typeof API_ENDPOINTS
export type AuthEndpoint = keyof typeof API_ENDPOINTS.AUTH
export type BooksEndpoint = keyof typeof API_ENDPOINTS.BOOKS
export type TemplatesEndpoint = keyof typeof API_ENDPOINTS.TEMPLATES
export type GitHubEndpoint = keyof typeof API_ENDPOINTS.GITHUB
export type AuditEndpoint = keyof typeof API_ENDPOINTS.AUDIT
export type SystemEndpoint = keyof typeof API_ENDPOINTS.SYSTEM

// Environment-specific configuration
export const API_CONFIG = {
  // Base URL for different environments
  BASE_URLS: {
    development: 'https://bpubm.com/api',
    staging: 'https://staging.bpubm.com/api',
    production: 'https://bpubm.com/api',
  },

  // Timeout settings
  TIMEOUT: 10000, // 10 seconds

  // Retry settings
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000, // 1 second
}

// Get current environment (you can enhance this based on your build process)
export const getCurrentEnvironment = (): keyof typeof API_CONFIG.BASE_URLS => {
  // You can enhance this logic based on your environment detection
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    if (hostname.includes('staging')) return 'staging'
    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) return 'development'
  }
  return 'production'
}

// Get base URL for current environment
export const getBaseUrl = (): string => {
  const env = getCurrentEnvironment()
  return API_CONFIG.BASE_URLS[env]
}

// Update API_BASE_URL based on environment (optional)
// export const API_BASE_URL = getBaseUrl()
